import React from 'react';
import { Form } from 'react-bootstrap';

// Services & Helpers
import UserService from 'services/UserService';
import SubAgencyService from 'services/SubAgencyService';
import BootboxHelper from 'helpers/BootboxHelper';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import SearchBox from 'components/common/SearchBox';
import EditUserModal from 'components/admin/users/EditUserModal';

//-------------------------------------------------------------------------------------------------------------------

class Users extends React.Component {

    constructor(props) {
        super(props);

        this.editUserModalRef = React.createRef();

        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        const { subAgency } = this.props;
        const users = await UserService.list({ subAgencyID: subAgency.id });
        this.setState({
            users,
            isLoading: false
        });
    }

    async addExistingUser(id) {
        if (this.state.isBusy) return;
        const { subAgency } = this.props;
        this.setState({ isLoading: true });
        const { success } = await SubAgencyService.addUser(subAgency.id, id, false);
        if (!success) {
            const confirm = await BootboxHelper.confirm('This user already belongs to another sub-agency. Adding them to this sub-agency will remove them from their current one. Continue?');
            if (confirm) {
                await SubAgencyService.addUser(subAgency.id, id, true);
            }
        }
        this.setState({ mode: null });
        this.load();
    }

    async addNewUser() {
        await this.editUserModalRef.current.show({
            role: 'Manager',
            subAgencyID: this.props.subAgency.id
        });
        this.load();
    }

    async editUser(user) {
        await this.editUserModalRef.current.show({
            userID: user.id
        });
        this.load();
    }
    
    async confirmRemoveUser(user) {
        const confirm = await BootboxHelper.confirm('Remove <b>' + TextHelpers.formatName(user.firstName, user.lastName) + '</b> from this sub-agency?');
        if (confirm) {
            this.removeUser(user.id);
        }
    }

    async removeUser(id) {
        if (this.state.isBusy) return;
        const { subAgency } = this.props;
        this.setState({ isLoading: true });
        await SubAgencyService.removeUser(subAgency.id, id);
        this.setState({ mode: null });
        this.load();
    }

    updateUserFields(id, fields) {
        const users = [...this.state.users];
        const index = users.findIndex(u => u.id == id);
        const user = { ...users[index] };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            user[fieldName] = value;
            UserService.updateNotificationField(id, fieldName, value);
        }
        users[index] = user;
        this.setState({ users });
    }

    render() {
        const { isLoading, users, mode } = this.state;
        if (isLoading) {
            return (<Loader fullPage />);
        }

        return (<>

            <div className="sub-agency-users-control mb-3">
                {!!mode &&
                    <button type="button" className="btn btn-secondary" onClick={() => this.setState({ mode: null })}>
                        <span className="fa fa-chevron-left" />{' '}
                        Cancel
                    </button>
                }

                {!mode && <>
                    <button type="button" className="btn btn-secondary" onClick={() => this.setState({ mode: 'add' })}>
                        <span className="fa fa-plus" />{' '}
                        Add existing user...
                    </button>

                    <button type="button" className="btn btn-secondary" onClick={() => this.addNewUser()}>
                        <span className="fa fa-plus" />{' '}
                        Add new manager user...
                    </button>

                    {users.length > 0 &&
                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ mode: 'remove' })}>
                            <span className="fa fa-times" />{' '}
                            Remove manager user...
                        </button>
                    }
                </>}

                {mode == 'add' &&
                    <SearchBox
                        types={['User']}
                        ignoreIDs={users.map(i => `user-${i.id}`)}
                        onSelect={item => {
                            const id = item.id.split('-')[1];
                            this.addExistingUser(id);
                        }}
                    />
                }

                {mode == 'remove' && 
                    <div className="alert alert-warning">
                        Select a manager user to remove them from this sub-agency...
                    </div>
                }
            </div>

            {users.length == 0 &&
                <div className="empty-text">
                    No manager users have been added to this sub-agency yet
                </div>
            }

            <div className="sticky-table-container scrolling">
                <table className="table table-bordered sticky-table selectable">
                    <thead>
                        <tr>
                            <th>First name</th>
                            <th>Last name</th>
                            <th>Email</th>
                            <th>Automated emails</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {

                            const onClick = (e) => {
                                if (mode == 'remove') this.confirmRemoveUser(user);
                                else this.editUser(user);
                            };

                            return (
                                <tr key={user.id}>
                                    <td onClick={onClick}>{user.firstName}</td>
                                    <td onClick={onClick}>{user.lastName}</td>
                                    <td onClick={onClick}>{user.email}</td>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            id="send-booking-emails"
                                            checked={!!user.sendBookingEmails}
                                            onChange={e => this.updateUserFields(user.id, { sendBookingEmails: e.target.checked })}
                                            label="Booking emails"
                                            disabled={mode == 'remove'}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="send-contract-emails"
                                            checked={!!user.sendContractEmails}
                                            onChange={e => this.updateUserFields(user.id, { sendContractEmails: e.target.checked })}
                                            label="Contract emails"
                                            disabled={mode == 'remove'}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="send-financial-emails"
                                            checked={!!user.sendFinancialEmails}
                                            onChange={e => this.updateUserFields(user.id, { sendFinancialEmails: e.target.checked })}
                                            label="Financial emails"
                                            disabled={mode == 'remove'}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <EditUserModal ref={this.editUserModalRef} />
            
        </>);
    }

}

export default Users;